/**
 * WebhookDiagnostics — Painel administrativo (master only) que exibe a fila
 * de eventos de webhook que foram descartados pelo backend. Ajuda a diagnosticar
 * problemas de integração Z-API sem precisar acessar logs do servidor.
 *
 * Motivos possíveis (`reason`):
 *   - unresolved_tenant     → instanceId veio mas não bate com nenhuma unit
 *   - missing_instance_id   → payload chegou sem instanceId (multi-master exige)
 *   - duplicate_message_id  → mesma mensagem já foi processada antes
 *   - status_callback_no_matching_send → callback pra send que não existe (mais)
 *   - unrecognized_event    → payload que não é inbound nem status conhecido
 *   - invalid_json          → body não parseou como JSON
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { RefreshCw, Trash2, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";

const REASON_LABELS = {
  unresolved_tenant: "Instância não cadastrada",
  missing_instance_id: "Sem instanceId no payload",
  duplicate_message_id: "Mensagem duplicada",
  status_callback_no_matching_send: "Callback sem envio correspondente",
  unrecognized_event: "Evento não reconhecido",
  invalid_json: "JSON inválido",
};

const REASON_COLORS = {
  unresolved_tenant: "bg-rose-100 text-rose-800 border-rose-200",
  missing_instance_id: "bg-amber-100 text-amber-800 border-amber-200",
  duplicate_message_id: "bg-sky-100 text-sky-800 border-sky-200",
  status_callback_no_matching_send: "bg-violet-100 text-violet-800 border-violet-200",
  unrecognized_event: "bg-slate-100 text-slate-700 border-slate-200",
  invalid_json: "bg-rose-100 text-rose-800 border-rose-200",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "medium" });
  } catch {
    return iso;
  }
}

export default function WebhookDiagnostics() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reasonFilter, setReasonFilter] = useState("all");
  const [instanceFilter, setInstanceFilter] = useState("");
  const [expanded, setExpanded] = useState({});
  const [unitStatus, setUnitStatus] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (reasonFilter && reasonFilter !== "all") params.reason = reasonFilter;
      if (instanceFilter) params.instance_id = instanceFilter;
      const { data } = await axios.get(`${API}/api/admin/webhook-drops`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats_24h || []);
      // Load unit status (best-effort — silent fail)
      try {
        const { data: us } = await axios.get(`${API}/api/admin/webhook-drops/unit-status`);
        setUnitStatus(us || []);
      } catch (_e) {
        setUnitStatus([]);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar drops");
    } finally {
      setLoading(false);
    }
  }, [API, reasonFilter, instanceFilter]);

  useEffect(() => { load(); }, [load]);

  async function del(dropId) {
    try {
      await axios.delete(`${API}/api/admin/webhook-drops/${dropId}`);
      toast.success("Descartado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  }

  async function clearAll() {
    if (!window.confirm("Apagar TODOS os drops registrados?")) return;
    try {
      const { data } = await axios.delete(`${API}/api/admin/webhook-drops`);
      toast.success(`${data.deleted} registro(s) apagado(s)`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha");
    }
  }

  return (
    <div className="space-y-6" data-testid="webhook-diagnostics-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-light tracking-tight" data-testid="page-title">Webhook Diagnostics</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Eventos que a Z-API mandou mas o sistema NÃO conseguiu processar. Não interrompem a operação —
            servem para diagnosticar por que uma mensagem inbound &quot;sumiu&quot; ou por que um status não atualizou.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button size="sm" variant="destructive" onClick={clearAll} disabled={total === 0} data-testid="clear-all-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Apagar todos
          </Button>
        </div>
      </div>

      {/* Status por unidade — barra visual */}
      {unitStatus.length > 0 && (
        <Card className="p-4" data-testid="unit-status-bar">
          <div className="text-xs uppercase text-muted-foreground mb-2">Status Z-API por unidade</div>
          <div className="flex flex-wrap gap-2">
            {unitStatus.map(u => {
              const map = {
                linked:            { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: "✅", label: "vinculada" },
                unlinked:          { cls: "bg-amber-100 text-amber-800 border-amber-200",     icon: "⚠️", label: "sem instância" },
                inactive_linked:   { cls: "bg-slate-100 text-slate-500 border-slate-200 line-through", icon: "🚫", label: "inativa (vinculada — bloqueada)" },
                inactive_unlinked: { cls: "bg-slate-100 text-slate-500 border-slate-200",     icon: "🚫", label: "inativa" },
              };
              const s = map[u.state] || map.unlinked;
              return (
                <div
                  key={u.unit_id}
                  className={`text-xs px-3 py-1.5 rounded border ${s.cls} flex items-center gap-2`}
                  data-testid={`unit-status-${u.unit_id}`}
                  title={u.instance_id_masked ? `instanceId: ${u.instance_id_masked}` : "Sem instância cadastrada"}
                >
                  <span>{s.icon}</span>
                  <span className="font-medium">{u.name}</span>
                  <span className="text-[10px] opacity-75">· {s.label}</span>
                  {u.instance_id_masked && (
                    <span className="text-[10px] font-mono opacity-60">({u.instance_id_masked})</span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Stats 24h */}
      {stats.length > 0 && (
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground mb-2">Últimas 24 h — por motivo</div>
          <div className="flex flex-wrap gap-2">
            {stats.map(s => (
              <button
                key={s.reason}
                onClick={() => setReasonFilter(s.reason === reasonFilter ? "all" : s.reason)}
                className={`text-xs px-2.5 py-1 rounded border ${REASON_COLORS[s.reason] || "bg-slate-100 text-slate-700 border-slate-200"} ${s.reason === reasonFilter ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                data-testid={`stat-${s.reason}`}
              >
                {REASON_LABELS[s.reason] || s.reason}: <b>{s.count}</b>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="text-xs text-muted-foreground">
          {total} total {reasonFilter !== "all" || instanceFilter ? "· filtrado" : ""}
        </div>
        <div className="flex-1" />
        <Select value={reasonFilter} onValueChange={setReasonFilter}>
          <SelectTrigger className="w-56 h-8 text-xs" data-testid="reason-filter">
            <SelectValue placeholder="Motivo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os motivos</SelectItem>
            {Object.entries(REASON_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          className="w-56 h-8 text-xs"
          placeholder="Filtrar por instanceId"
          value={instanceFilter}
          onChange={(e) => setInstanceFilter(e.target.value)}
          data-testid="instance-filter"
        />
      </div>

      {/* Lista */}
      <Card className="divide-y" data-testid="drops-list">
        {items.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <AlertCircle className="h-6 w-6 text-emerald-500" />
            <div>Nenhum drop registrado. Integração saudável ✨</div>
          </div>
        )}
        {items.map((it) => {
          const isExpanded = expanded[it.drop_id];
          return (
            <div key={it.drop_id} className="p-3 hover:bg-slate-50" data-testid={`drop-row-${it.drop_id}`}>
              <div className="flex items-start gap-3">
                <button
                  className="mt-0.5"
                  onClick={() => setExpanded(x => ({ ...x, [it.drop_id]: !isExpanded }))}
                  aria-label="expandir"
                  data-testid={`expand-${it.drop_id}`}
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </button>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className={`text-[10px] ${REASON_COLORS[it.reason] || ""}`}>
                      {REASON_LABELS[it.reason] || it.reason}
                    </Badge>
                    <span className="text-xs text-muted-foreground">{fmtDate(it.created_at)}</span>
                    {it.instance_id && (
                      <code className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                        {it.instance_id}
                      </code>
                    )}
                    {!it.instance_id && it.reason === "missing_instance_id" && (
                      <span className="text-[10px] text-amber-700">(sem instanceId)</span>
                    )}
                    {it.master_hint && (
                      <span className="text-[10px] text-violet-700">hint: {it.master_hint}</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 truncate">
                    {it.summary?.text_preview || (
                      <span className="text-muted-foreground italic">
                        {[
                          it.summary?.type && `type=${it.summary.type}`,
                          it.summary?.status && `status=${it.summary.status}`,
                          it.summary?.messageId && `msg=${it.summary.messageId}`,
                          it.summary?.phone && `phone=${it.summary.phone}`,
                        ].filter(Boolean).join(" · ") || "sem preview"}
                      </span>
                    )}
                  </div>
                  {isExpanded && (
                    <div className="mt-2 p-2 rounded bg-slate-900 text-slate-100 text-[10px] font-mono overflow-x-auto whitespace-pre-wrap break-all" data-testid={`raw-${it.drop_id}`}>
                      {it.raw_preview || JSON.stringify(it.summary, null, 2)}
                    </div>
                  )}
                </div>
                <Button size="sm" variant="ghost" onClick={() => del(it.drop_id)} data-testid={`delete-${it.drop_id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                </Button>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
